import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useImperativeHandle } from 'react';
import { Trash2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from 'src/lib/utils';

import {
  type IAdditionalFieldType,
  apiCredentialsFormSchema,
  type IApiCredentialsForm,
  secretTypeOptions,
} from '../../model';

import MoreOptionsDropdown from './MoreOptionsDropdown';
import DynamicFieldInput from './DynamicFieldInput';

type Props = {
  ref: React.RefObject<{ submit: (callback: (data: IApiCredentialsForm) => void) => void } | null>;
};

const SecretTypeSelect = ({ value, onValueChange }: { value: string; onValueChange: (value: string) => void }) => {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <FormControl>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a secret type" />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        {secretTypeOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const ApiCredentialsForm = ({ ref }: Props) => {
  const form = useForm<IApiCredentialsForm>({
    resolver: zodResolver(apiCredentialsFormSchema),
    defaultValues: {
      name: '',
      providerName: '',
      secret: '',
      type: '',
      url: '',
      description: '',
      otherType: undefined,
      additionalFields: [],
    },
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'additionalFields',
  });

  useImperativeHandle(
    ref,
    () => ({
      submit: (callback) => form.handleSubmit(callback)(),
    }),
    [],
  );

  const typeField = form.watch('type');

  const onSelectMoreOption = (type: IAdditionalFieldType) => append({ type, value: '' });

  const onClearOtherType = () => {
    form.setValue('otherType', undefined);
    form.setValue('type', '');
  };

  return (
    <div className="space-y-4">
      <Form {...form}>
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Credential name</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>
                Add an asset name to easily identify these credentials in the deposit table.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="providerName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provider</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>Add the name of the provider these credentials access.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="secret"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Secret</FormLabel>
              <FormControl>
                <Textarea {...field} className="resize-none h-[80px]" />
              </FormControl>
              <FormDescription>Paste your password, token, or secret here.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {typeField !== 'other' && (
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <SecretTypeSelect value={field.value} onValueChange={field.onChange} />
                <FormDescription>Select or enter a secret type.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {typeField === 'other' && (
          <FormField
            control={form.control}
            name="otherType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <FormControl>
                  <div className="flex items-center gap-2">
                    <Input {...field} />
                    <Button variant="destructive" size="icon" className="shrink-0" onClick={onClearOtherType}>
                      <Trash2 />
                    </Button>
                  </div>
                </FormControl>
                <FormDescription>Select or enter a secret type.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instance URL</FormLabel>
              <FormControl>
                <Input {...field} placeholder="https://yourcompany.serviceprovider.com" />
              </FormControl>
              <FormDescription>
                Add the specific web address of this cloud service where you use these login details for authentication.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} className="resize-none h-[80px]" />
              </FormControl>
              <FormDescription>
                Describe what this account is used for, and the role it plays in recovering application functionality.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Separator />
        {fields.map((field, index) => (
          <FormField
            key={field.id}
            control={form.control}
            name={`additionalFields.${index}.value`}
            render={({ field: inputField }) => (
              <FormItem>
                <FormLabel className={cn('capitalize', field.type === 'url' && 'uppercase')}>{field.type}</FormLabel>
                <div className="flex items-center gap-2">
                  <DynamicFieldInput type={field.type} field={inputField} />
                  <Button variant="destructive" size="icon" className="shrink-0" onClick={() => remove(index)}>
                    <Trash2 />
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
        <MoreOptionsDropdown onSelectOption={onSelectMoreOption} />
      </Form>
    </div>
  );
};

export default ApiCredentialsForm;
